import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { EVENTS, identifyUser, resetAnalytics, track } from "@/lib/analytics";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Track the last id we identified to PostHog so re-running getSession()
  // or every TOKEN_REFRESHED event doesn't re-fire identify(). PostHog
  // dedupes internally, but skipping the call entirely keeps the event
  // log clean.
  const lastIdentifiedRef = useRef<string | null>(null);

  // Build the person-properties payload we ship to PostHog on identify.
  // PostHog uses `email` (no $-prefix) and `name` for display in the
  // Persons table, without these, every Person renders as the bare
  // distinct_id (the Supabase user UUID). full_name comes from the
  // user_metadata blob we set in signUp(). signed_up_at lets you cohort
  // users by signup date in PostHog without a separate event.
  const buildPersonProperties = (user: User): Record<string, string | undefined> => {
    const fullName = (user.user_metadata?.full_name as string | undefined) ?? undefined;
    return {
      email: user.email ?? undefined,
      // Set both the unprefixed and $-prefixed name keys, PostHog's
      // Person header falls back across `email`, `name`, `$name`, and
      // `username` depending on which is populated, so covering both
      // keeps the display sensible even if the project's defaults change.
      name: fullName,
      $name: fullName,
      signed_up_at: user.created_at ?? undefined,
    };
  };

  // Gate onAuthStateChange session/user updates until getUser() validates
  // the initial hydrate. Without this, a stale localStorage JWT makes
  // `user` truthy before the server rejects it — landing shows "Dashboard"
  // and /login auto-redirects into protected routes that then fail.
  const initialValidationDoneRef = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (initialValidationDoneRef.current) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user && lastIdentifiedRef.current !== session.user.id) {
          identifyUser(session.user.id, buildPersonProperties(session.user));
          lastIdentifiedRef.current = session.user.id;
        }
      }

      if (event === "SIGNED_OUT") {
        track(EVENTS.USER_SIGNED_OUT);
        resetAnalytics();
        lastIdentifiedRef.current = null;
      }

      // Supabase ignores the /reset-password path in redirectTo and sends users
      // to the Site URL (root). Catch the PASSWORD_RECOVERY event wherever it
      // fires and redirect to the reset-password page ourselves.
      if (event === "PASSWORD_RECOVERY" && window.location.pathname !== "/reset-password") {
        window.location.replace("/reset-password");
      }
    });

    void (async () => {
      const { data: { session: localSession } } = await supabase.auth.getSession();
      const { data: { user: validatedUser }, error } = await supabase.auth.getUser();

      if (validatedUser) {
        setSession(localSession);
        setUser(validatedUser);
        if (lastIdentifiedRef.current !== validatedUser.id) {
          identifyUser(validatedUser.id, buildPersonProperties(validatedUser));
          lastIdentifiedRef.current = validatedUser.id;
        }
      } else if (error) {
        const looksLikeAuthFailure =
          error.status === 401 ||
          error.status === 403 ||
          /jwt|token|session|expired|invalid/i.test(error.message);
        const looksLikeNetworkFailure =
          error.status === 0 ||
          (error.status !== undefined && error.status >= 500) ||
          /fetch|network|timeout|failed to fetch/i.test(error.message);

        if (looksLikeAuthFailure && localSession) {
          await supabase.auth.signOut({ scope: "local" });
          setSession(null);
          setUser(null);
        } else if (looksLikeNetworkFailure && localSession) {
          // Transient outage — keep the local session so a signed-in
          // user isn't logged out because getUser() couldn't reach the server.
          setSession(localSession);
          setUser(localSession.user ?? null);
          if (
            localSession.user &&
            lastIdentifiedRef.current !== localSession.user.id
          ) {
            identifyUser(localSession.user.id, buildPersonProperties(localSession.user));
            lastIdentifiedRef.current = localSession.user.id;
          }
        } else {
          setSession(null);
          setUser(null);
        }
      } else if (localSession?.user) {
        // getUser returned no user and no error (unusual) but localStorage
        // still has a session blob — don't trust it for nav/redirect decisions.
        await supabase.auth.signOut({ scope: "local" });
        setSession(null);
        setUser(null);
      } else {
        setSession(null);
        setUser(null);
      }

      initialValidationDoneRef.current = true;
      setLoading(false);
    })();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    // Confirm-signup email links land on /welcome, a branded welcome
    // page mirroring the welcome email's tone. Clicking its CTA bounces
    // through ProtectedRoute to /onboarding. Without this, Supabase
    // falls back to the project's Site URL (landing page), which
    // discards the momentum and leaves the user wondering what's next.
    const { error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/welcome`,
      },
    });

    if (!error) {
      // Email + name on the event itself, `signUp` doesn't create an
      // active session (we require email confirmation), so the auth
      // listener's identify() won't fire until the user later logs in.
      // Without these properties, this signup event would land
      // attributed to an anonymous distinct_id with no way to tell
      // who it belonged to. Also gives us the email in PostHog's
      // event-explorer view straight away.
      track(EVENTS.USER_SIGNED_UP, {
        method: "email",
        email: normalizedEmail,
        name: fullName,
      });
    }

    // MS101: same bug MS66 fixed in Onboarding — was a raw fetch()
    // against a hardcoded fallback URL with no `apikey` header, which
    // silently 401'd at the Supabase edge gateway (the gateway
    // requires apikey on every function call regardless of verify_jwt
    // setting). So every signup's welcome email was almost certainly
    // failing without a trace. Switched to supabase.functions.invoke()
    // which handles auth headers via the shared client. Errors are
    // now logged via console.warn so production has some signal
    // instead of swallowing every failure.
    if (!error) {
      void supabase.functions
        .invoke("send-email", {
          body: {
            type: "welcome",
            to: normalizedEmail,
            vars: {
              firstName: fullName.split(" ")[0],
              dashboardUrl: `${window.location.origin}/dashboard`,
            },
          },
        })
        .then(({ error: emailErr }) => {
          if (emailErr) {
            console.warn("welcome email failed:", emailErr.message);
          }
        });
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const { data, error } = await supabase.auth.signInWithPassword({
      email: normalizedEmail,
      password,
    });
    if (!error && data.session) {
      // Apply the session synchronously — don't wait for onAuthStateChange.
      // Login.tsx navigates to /dashboard immediately after signIn()
      // resolves; ProtectedRoute reads session from this context. Without
      // this, React state is still null for one render and the guard
      // bounces the user straight back to /login ("can't sign in" even
      // though Supabase accepted the credentials).
      setSession(data.session);
      setUser(data.session.user);
      setLoading(false);
      if (lastIdentifiedRef.current !== data.session.user.id) {
        identifyUser(data.session.user.id, buildPersonProperties(data.session.user));
        lastIdentifiedRef.current = data.session.user.id;
      }
      track(EVENTS.USER_LOGGED_IN, { method: "email", email: normalizedEmail });
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
